import { all, put, takeLatest, select, call } from "@redux-saga/core/effects";
import { RootReducer } from "@twire/redux/types";
import { filterPpt, getPaginated } from "./actions";
import * as actionTypes from "./actionTypes";
import { publicQuery } from "../../graphql/requestHelper";
import { getTournaments } from "../../graphql/public/queries";
import { formatTournamentData } from "../tournaments/functions";
import { CACHE_TIME } from "../../graphql/cache";
import { PptType } from "../tournaments/types";

const buildPptRequest = (pptType: PptType, filter = {}) => {
  const actionsTypeReq = {
    ppt_scrim: actionTypes.PPT_GET_PPT_SCRIM_REQUESTED,
    ppt_cup: actionTypes.PPT_GET_PPT_CUP_REQUESTED,
    ppt_master: actionTypes.PPT_GET_PPT_MASTER_REQUESTED
  };

  return function* buildPpt() {
    yield put({
      type: actionsTypeReq[pptType],
      payload: { key: pptType }
    });

    const state: RootReducer = yield select((state: RootReducer) => state);
    try {
      const ppt: Generator<any, any> = yield publicQuery(
        getTournaments,
        {
          game: state.app.game,
          type: pptType,
          ...state.ppt.ppt[pptType].filter,
          ...filter
        },
        CACHE_TIME.MINUTE * 10
      );

      const formattedPpt = (ppt as any).data.getTournaments.map(
        (tournament: any) => formatTournamentData(tournament)
      );

      const actionsTypeSuccs = {
        ppt_scrim: actionTypes.PPT_GET_PPT_SCRIM_SUCCESS,
        ppt_cup: actionTypes.PPT_GET_PPT_CUP_SUCCESS,
        ppt_master: actionTypes.PPT_GET_PPT_MASTER_SUCCESS
      };

      yield put({
        type: actionsTypeSuccs[pptType],
        payload: {
          key: pptType,
          ppt: {
            game: state.app.game,
            data: formattedPpt,
            page: (filter as any)?.page || 0,
          },
        },
        paginated: !!(filter as any)?.page,
      });
    } catch (error) {
      const actionsTypeErr = {
        ppt_scrim: actionTypes.PPT_GET_PPT_SCRIM_FAILED,
        ppt_cup: actionTypes.PPT_GET_PPT_CUP_FAILED,
        ppt_master: actionTypes.PPT_GET_PPT_MASTER_FAILED
      };

      yield put({
        type: actionsTypeErr[pptType],
        payload: { key: pptType },
        error
      });
    }
  };
};

function* applyFilter({ filter, key }: ReturnType<typeof filterPpt>) {
  yield put({
    type: actionTypes.PPT_FILTER_PPT_REQUESTED,
    payload: {
      key
    }
  });
  try {
    yield call(buildPptRequest(key, { filter }));
    yield put({
      type: actionTypes.PPT_FILTER_PPT_SUCCESS,
      payload: {
        filter,
        key
      }
    });
  } catch (error) {
    yield put({
      type: actionTypes.PPT_FILTER_PPT_FAILED,
      payload: {
        key
      }
    });
  }
}

function* fetchPaginated({ page, key }: ReturnType<typeof getPaginated>) {
  yield call(buildPptRequest(key, { page }));
}

const fetchPptScrim = buildPptRequest("ppt_scrim");
const fetchPptCup = buildPptRequest("ppt_cup");
const fetchPptMaster = buildPptRequest("ppt_master");

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function* rootSaga() {
  yield all([
    takeLatest(actionTypes.PPT_GET_PPT_SCRIM, fetchPptScrim),
    takeLatest(actionTypes.PPT_GET_PPT_CUP, fetchPptCup),
    takeLatest(actionTypes.PPT_GET_PPT_MASTER, fetchPptMaster),
    takeLatest(actionTypes.PPT_FILTER_PPT, applyFilter),
    takeLatest(actionTypes.PPT_GET_PPT_PAGINATED, fetchPaginated)
  ]);
}
